import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Formik } from "formik";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import { groupTypesManagement } from "../../../../../../hooks/api/GroupTypesManagement/GroupTypesManagement";
import { DefaultLayout } from "../../../../../../layouts/Default";
import { TopNavigation } from "../../../../../../themes/Default/components/TopNavigation";
import { Button, ButtonBack } from "../../../../../../themes/Default/components/TopNavigation/components/Button";
import { Tabs } from "../../../../../../themes/Default/components/Tabs";
import { Main } from "../../../../../../themes/Default/components/Main";
import { Breadcrumps } from "../../../../../../themes/Default/components/Breadcrumps";
import { Link } from "../../../../../../themes/Default/components/Breadcrumps/components/Link";
import { Form } from "../../../../../../themes/Default/components/Form";
import { Row } from "../../../../../../themes/Default/components/Form/components/Row";
import { Field } from "../../../../../../themes/Default/components/Form/components/Row/components/Field";
import { Input } from "../../../../../../themes/Default/components/Form/components/Input";
import ColorPicker from "../../../../../../themes/Default/components/Form/components/ColorPicker/ColorPicker";
import IconPicker from "../../components/IconPicker/IconPicker";
import { Select } from "../../../../../../themes/Default/components/Form/components/Select";
import { prepareSelect } from "../../../../../../services/core/Select";
import SummerNote from "../../../../../../packages/SummerNote/SummerNote";

const EditType = () => {
    const { t } = useTranslation("common");
    const formRef = useRef(null);
    const navigate = useNavigate();
    const {groupTypeId} = useParams();
    const [formErrors, setFormErrors] = useState([]);
    const { useViewGroupTypesMutation, useUpdateGroupTypesMutation } = groupTypesManagement;
    const [ viewType ] = useViewGroupTypesMutation();
    const [ updateType ] = useUpdateGroupTypesMutation();
    const [saving, setSaving] = useState(false);

    const tabs = [
        { title: t("plans.title"), onClick: () => gotoPlansTab() },
        { title: t("groupTypes.title"), isActive: true },
        { title: t("groups.progress"), onClick: () => gotoProgressTab() },
        { title: t("groups.progressTest"), onClick: () => gotoProgressTestTab() },
    ];

    const gotoPlansTab = () => {
        navigate("/settings/groups/plans");            
    };

    const gotoProgressTab = () => {
        navigate("/settings/groups/progress");
    };

    const gotoProgressTestTab = () => {
        navigate("/settings/groups/progress-tests");
    };

    const onSave = () => {
        if (formRef.current) {
            setSaving(true);
            formRef.current.handleSubmit();
        }
    };

    const onSaved = (values) => {
        const body = { ...values };
        updateType({ body, groupTypeId }).then((result) => {
            if (result?.error) {
                setFormErrors(result?.error?.data?.error?.violations);
                toast.error(result.error.data.error.responseMessage);
            } else {
                toast.info(result?.data?.info?.responseMessage);
            }
            setSaving(false);
        });
    };

    useEffect(() => {
        viewType(groupTypeId).then((result) => {
            console.log(result);
            formRef.current.setValues(result?.data);
        });
    }, []);

    return (
        <DefaultLayout>
            
            <TopNavigation>
                <ButtonBack to="/settings/groups/types" />
                <Button title={t("save")} icon="fa-regular fa-floppy-disk" onClick={onSave}></Button>
            </TopNavigation>
            <Tabs tabs={tabs} />
            <Main>
                <Breadcrumps>
                    <Link title={t("settings.title")} icon="fa-gear" />
                    <Link title={t("groups.title")} />
                    <Link title={t("groupTypes.title")} />
                    <Link title={t("Izmeni tip")} isActive={true} />
                </Breadcrumps>
                <Formik
                    innerRef={formRef}
                    initialValues={{
                        name: "",
                        hasTests: null,
                        hasReport: null,
                        hasProgress: null,
                        minStudents: null,
                        maxStudents: null,
                        contractText: ""
                    }}
                    onSubmit={onSaved}
                >
                    {({ values, handleChange }) => (
                        <Form>
                            <Row>
                                <Field>
                                    <Input
                                        name="name"
                                        label={t("noteTypes.name")}
                                        required={true}
                                        value={values.name}
                                        onChange={handleChange}
                                        error={formErrors?.name}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="hasTests"
                                        label={t("Ima test")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("yes") },
                                            { id: false, name: t("no") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.hasTests}
                                        error={formErrors?.hasTests}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="hasPlan"
                                        label={t("Ima plan")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("yes") },
                                            { id: false, name: t("no") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.hasPlan}
                                        error={formErrors?.hasPlan}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Select
                                        name="hasProgress"
                                        label={t("Ima napredak")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("yes") },
                                            { id: false, name: t("no") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.hasProgress}
                                        error={formErrors?.hasProgress}
                                        searchVisible={false}
                                    />
                                </Field>
                                <Field>
                                    <Select
                                        name="hasReport"
                                        label={t("Ima izveštaj")}
                                        required={true}
                                        options={prepareSelect([
                                            { id: true, name: t("yes") },
                                            { id: false, name: t("no") },
                                        ])}
                                        onChange={(e) => {
                                            handleChange(e);
                                        }}
                                        selectedValue={values.hasReport}
                                        error={formErrors?.hasReport}
                                        searchVisible={false}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <ColorPicker
                                        name="color"
                                        label={t("noteTypes.color")}
                                        value={values.color}
                                        onChange={handleChange}
                                        error={formErrors?.color}
                                        disabled={true}
                                        hint={t("optionNotAllowed")}
                                    />
                                </Field>
                                <Field>
                                    <IconPicker
                                        name="icon"
                                        label={"Ikonica"}
                                        onChange={handleChange}
                                        selectedValue={values.icon}
                                        error={formErrors?.icon}
                                        searchVisible={false}
                                        disabled={true}
                                        hint={t("optionNotAllowed")}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field>
                                    <Input
                                        name="minStudents"
                                        label={t("Minimalno polaznika")}
                                        required={true}
                                        value={values.minStudents}
                                        onChange={handleChange}
                                        error={formErrors?.minStudents}
                                    />
                                </Field>
                                <Field>
                                    <Input
                                        name="maxStudents"
                                        label={t("Maksimalno polaznika")}
                                        required={true}
                                        value={values.maxStudents}
                                        onChange={handleChange}
                                        error={formErrors?.maxStudents}
                                    />
                                </Field>
                            </Row>
                            <Row>
                                <Field isFullWidth={true}>
                                    <SummerNote
                                        saving={saving}
                                        height="1000px"
                                        name="contractText"
                                        label={t("Ugovor")}
                                        value={values.contractText}
                                        onChange={handleChange}
                                        error={formErrors?.contractText}
                                    />
                                </Field>
                            </Row>
                        </Form>
                    )}
                </Formik>
            </Main>
        </DefaultLayout>
    );
};

export default EditType;
